import { makeAutoObservable, runInAction } from 'mobx'
import { MaterialsService } from '~/shared/api/services/materials'
import { handleAxiosError } from '~/shared/lib'
import {
  MaterialsLoadingType,
  MaterialsPageParams,
  MaterialsType,
} from './types'
import { INITIAL_FORM_MATERIALS } from '../constants/initial-form-materials'

export class MaterialsStore {
  materials: MaterialsType[] = []

  formDataMaterial: MaterialsType = INITIAL_FORM_MATERIALS

  loaders: MaterialsLoadingType = {
    materials: false,
    loadMoreMaterials: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  searchParams = {
    q: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof MaterialsLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof MaterialsLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  updateSearchParam = <K extends keyof typeof this.searchParams>(
    key: K,
    value: (typeof this.searchParams)[K],
  ) => {
    this.searchParams[key] = value
  }

  getMaterials = async (options?: { action?: 'loadMoreMaterials' }) => {
    const { action } = options || {}
    this.activeLoader(action || 'materials')

    try {
      const params: MaterialsPageParams = {
        limit: this.requestSettings.limit,
        offset:
          action === 'loadMoreMaterials' ? this.requestSettings.offset : 0,
      }

      if (this.searchParams.q?.trim()) {
        params.q = this.searchParams.q.trim()
      }

      const { data } = await MaterialsService.getMaterials({
        params,
      })

      if (action === 'loadMoreMaterials') {
        runInAction(() => {
          this.materials = [...this.materials, ...data]
        })
      } else {
        runInAction(() => {
          this.materials = data
        })
      }
      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreMaterials'
              ? this.requestSettings.offset + this.requestSettings.limit
              : 20,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'materials')
    }
  }

  getMaterialById = (material: MaterialsType) => {
    this.formDataMaterial = material
  }

  clearFormData = () => {
    this.formDataMaterial = INITIAL_FORM_MATERIALS
  }

  updateFormField = (languageCode: string, value: string) => {
    const normalizedCode = languageCode.toUpperCase()
    const currentLocalizations = this.formDataMaterial.localizations
    const existingLocalization = currentLocalizations.some(
      (local) => local.languageCode.toUpperCase() === normalizedCode,
    )

    this.formDataMaterial = {
      ...this.formDataMaterial,
      localizations: existingLocalization
        ? currentLocalizations.map((local) =>
            local.languageCode.toUpperCase() === normalizedCode
              ? { ...local, description: value }
              : local,
          )
        : [
            ...currentLocalizations,
            {
              product_material_id: this.formDataMaterial.id || '',
              description: value,
              languageCode: normalizedCode,
            },
          ],
    }
  }
}
